import { useEffect, useState, useCallback } from 'react';
import {
  Key, Plus, RefreshCw, Trash2, Copy, Check, XCircle,
  Shield, ChevronDown, ChevronUp, Search, Pencil, Crown,
} from 'lucide-react';
import { apiTokenApi, rbacApi } from '../services/api';
import { useProjectStore } from '../store/useProjectStore';
import { useOrgHierarchyStore } from '../store/useOrgHierarchyStore';
import { useUserStore } from '../store/useUserStore';
import AuthGuard from '../components/auth/AuthGuard';
import TokenWizard from '../components/access/TokenWizard';
import RefreshButton from '../components/common/RefreshButton';
import toast from 'react-hot-toast';

interface ApiToken {
  _id: string;
  name: string;
  description: string;
  accountType?: 'standard' | 'super_admin';
  projectId: string;
  projectIds?: string[];
  ownerUserId?: string;
  permissions: string[];
  allowedTypes: string[];
  allowedRoutes?: string[];
  tokenPrefix: string;
  /** 'training' = acts as its owner on one training programme (EP-26 FE-16). */
  kind?: string;
  programId?: string;
  expiresAt?: string | null;
  active: boolean;
  lastUsed: string | null;
  createdAt: string;
  updatedAt: string;
  token?: string; // Only present on create/regenerate
}

// Every permission the API accepts. Four (links:*, releases:*) were missing
// here, so tokens carrying them could not be edited without silently dropping
// them — the editor would post back only what it knew about.
const ALL_PERMISSIONS = [
  { id: 'workitems:read', label: 'Read Work Items' },
  { id: 'workitems:create', label: 'Create Work Items' },
  { id: 'workitems:update', label: 'Update Work Items' },
  { id: 'activities:read', label: 'Read Activities' },
  { id: 'activities:create', label: 'Create Activities' },
  { id: 'projects:read', label: 'Read Projects' },
  { id: 'links:read', label: 'Read Commit/PR Links' },
  { id: 'links:create', label: 'Create Commit/PR Links' },
  { id: 'releases:read', label: 'Read Releases' },
  { id: 'releases:write', label: 'Manage Releases' },
  { id: 'sprints:read', label: 'Read PIs and Sprints' },
  { id: 'sprints:write', label: 'Plan PIs and Sprints' },
  // A service (hs-ways-to-work) asking whether a PERSON may act: POST /api/rbac/check.
  { id: 'rbac:check', label: "Check a Person's Access (services)" },
  { id: 'comments:create', label: 'Comment on Work Items' },
  // Deliberately not implied by Create Work Items: an intake token must
  // not be able to file arbitrary work.
  { id: 'leads:create', label: 'Submit Leads' },
  // Vendor Research raising held-evidence approvals; deciding stays a person's (EP-44 FE-1).
  { id: 'approvals:evidence', label: 'Raise and Follow Research Evidence Approvals (services)' },
  { id: 'usage:record', label: 'Send Monthly Usage Records to the CRM (services)' },
];

/**
 * Every project a token declares — the UI mirror of
 * `token_scope.declared_project_ids` on the server.
 *
 * Both fields count for every account type. The old rule (projectIds for
 * super_admin, projectId for standard) is what made a standard token set to
 * three projects silently keep one.
 */
function declaredProjects(t: { projectId?: string; projectIds?: string[] }): string[] {
  const ids = (t.projectIds || []).filter(Boolean);
  if (t.projectId && !ids.includes(t.projectId)) return [t.projectId, ...ids];
  return ids;
}

const ALLOWED_TYPES = [
  { id: 'epic', label: 'Epic' },
  { id: 'feature', label: 'Feature' },
  { id: 'story', label: 'Story' },
  { id: 'task', label: 'Task' },
  { id: 'bug', label: 'Bug' },
  { id: 'security', label: 'Security' },
  { id: 'idea', label: 'Idea' },
  { id: 'lead', label: 'Lead' },
  { id: 'opportunity', label: 'Opportunity' },
];

export default function AdminApiTokensPage() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [projectGroups, setProjectGroups] = useState<any[]>([]);
  const [editingToken, setEditingToken] = useState<ApiToken | null>(null);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const projects = useProjectStore((s) => s.projects);
  const organizations = useOrgHierarchyStore((s) => s.organizations);
  const users = useUserStore((s) => s.users);
  const fetchUsers = useUserStore((s) => s.fetchUsers);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiTokenApi.list();
      setTokens(data);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
    fetchUsers();
    // Project groups let the wizard offer an existing grouping before making
    // someone tick 60 boxes — the enumeration that made the access page
    // unreadable started exactly there.
    rbacApi.listProjectGroups().then(setProjectGroups).catch(() => {});
  }, [fetchTokens, fetchUsers]);

  const handleRevoke = async (token: ApiToken) => {
    if (!confirm(`Revoke token "${token.name}"? It will no longer be usable for API access.`)) return;
    try {
      await apiTokenApi.revoke(token._id);
      toast.success(`Token "${token.name}" has been revoked`);
      fetchTokens();
    } catch (err: any) {
      toast.error(err.message || 'Failed to revoke token');
    }
  };

  const handleDelete = async (token: ApiToken) => {
    if (!confirm(`Permanently delete token "${token.name}"? This cannot be undone.`)) return;
    try {
      await apiTokenApi.delete(token._id);
      toast.success(`Token "${token.name}" has been deleted`);
      fetchTokens();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete token');
    }
  };

  const handleRegenerate = async (token: ApiToken) => {
    if (!confirm(`Regenerate token "${token.name}"? The current token will be immediately invalidated.`)) return;
    try {
      const result = await apiTokenApi.regenerate(token._id);
      setRevealedToken(result.token);
      setExpandedId(token._id);
      toast.success('Token regenerated — copy the new token now');
      fetchTokens();
    } catch (err: any) {
      toast.error(err.message || 'Failed to regenerate token');
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Copied to clipboard');
  };

  // Returns '' rather than undefined for a token with no project. A token
  // scoped purely through `projectIds` has no `projectId` at all — the wizard
  // never sets one — and this used to hand `undefined` straight to
  // `.toLowerCase()` in the search filter, blanking the whole page.
  const getProjectName = (projectId?: string) => {
    if (!projectId) return '';
    return projects.find((p) => p._id === projectId)?.name || projectId;
  };

  const getOrgForProject = (projectId: string) => {
    const project = projects.find((p) => p._id === projectId);
    if (!project?.organizationId) return null;
    return organizations.find((o) => o._id === project.organizationId);
  };

  const getOwnerName = (ownerUserId?: string) => {
    if (!ownerUserId) return null;
    const user = users.find((u) => u._id === ownerUserId);
    return user ? user.name || user.email : ownerUserId;
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return 'Never';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  // One haystack, built defensively. Every field here is optional on at least
  // some live token — `description` is absent on older ones, `projectId` on any
  // token scoped through `projectIds` — and a single undefined took down the
  // whole page rather than just failing to match.
  const filteredTokens = tokens.filter((t) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    return [
      t.name,
      t.description,
      t.accountType,
      getProjectName(t.projectId),
      ...(t.projectIds || []).map(getProjectName),
    ].some((field) => String(field ?? '').toLowerCase().includes(needle));
  });

  return (
    <AuthGuard roles={['super_admin']}>
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center">
              <Key size={20} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">API Tokens</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage service account tokens for external tool integration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RefreshButton
              onRefresh={async () => { await fetchTokens(); }}
              tooltip="Refresh tokens"
            />
            {/* Guided is the default path. The raw dialog stays for editing
                and for anyone who knows exactly what they want. */}
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Advanced
            </button>
            <button
              onClick={() => setShowWizard(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-accent hover:bg-accent/90 transition-colors shadow-sm"
            >
              <Plus size={16} />
              Create Token
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tokens..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Tokens</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tokens.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Active</p>
            <p className="text-2xl font-bold text-emerald-600">{tokens.filter((t) => t.active).length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Revoked</p>
            <p className="text-2xl font-bold text-red-500">{tokens.filter((t) => !t.active).length}</p>
          </div>
        </div>

        {/* Token List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTokens.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <Key size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No API tokens found</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Create one to enable external tool integration</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTokens.map((token) => {
              const org = getOrgForProject(token.projectId);
              const isExpanded = expandedId === token._id;
              return (
                <div
                  key={token._id}
                  className={`bg-white dark:bg-gray-800 rounded-xl border transition-colors ${
                    token.active
                      ? 'border-gray-200 dark:border-gray-700'
                      : 'border-red-200 dark:border-red-800/30 opacity-60'
                  }`}
                >
                  {/* Token Row */}
                  <div className="flex items-center gap-4 px-4 py-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      token.active ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-500'
                    }`}>
                      <Key size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{token.name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          token.active
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          {token.active ? 'Active' : 'Revoked'}
                        </span>
                        {token.kind === 'training' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300"
                                title={`Training token for ${token.programId}: acts as its owner, view and operate only`}>
                            Training
                          </span>
                        )}
                        {token.expiresAt && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${new Date(token.expiresAt) < new Date()
                            ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {new Date(token.expiresAt) < new Date() ? 'Expired' : `Expires ${String(token.expiresAt).slice(0, 10)}`}
                          </span>
                        )}
                        {token.accountType === 'super_admin' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 flex items-center gap-0.5">
                            <Crown size={9} />
                            Super Admin
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {/* Every declared project, whatever the account type
                              — mirrors token_scope.declared_project_ids. This
                              showed projectIds only for super_admin tokens, so
                              a standard token scoped to three projects listed
                              none of them. */}
                          {declaredProjects(token).map(getProjectName).filter(Boolean).join(', ') || '—'}
                          {token.accountType !== 'super_admin' && org && <span className="text-gray-400 dark:text-gray-500"> · {org.name}</span>}
                        </span>
                        {token.ownerUserId && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            Owner: {getOwnerName(token.ownerUserId)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Prefix: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{token.tokenPrefix}...</code>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : token._id)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
                        title="Details"
                      >
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {token.active && (
                        <>
                          <button
                            onClick={() => setEditingToken(token)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-accent transition-colors"
                            title="Edit token"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleRegenerate(token)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-amber-500 transition-colors"
                            title="Regenerate token"
                          >
                            <RefreshCw size={16} />
                          </button>
                          <button
                            onClick={() => handleRevoke(token)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-red-500 transition-colors"
                            title="Revoke token"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(token)}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete token"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">
                      {/* Show newly created/regenerated token */}
                      {revealedToken && expandedId === token._id && (
                        <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                          <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">
                            New Token — copy now, it won't be shown again
                          </p>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-white dark:bg-gray-800 px-2 py-1.5 rounded border border-amber-200 dark:border-amber-700 text-gray-900 dark:text-gray-100 break-all">
                              {revealedToken}
                            </code>
                            <button
                              onClick={() => copyToClipboard(revealedToken, token._id)}
                              className="p-1.5 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-800/30 text-amber-600"
                              title="Copy token"
                            >
                              {copiedId === token._id ? <Check size={14} /> : <Copy size={14} />}
                            </button>
                          </div>
                        </div>
                      )}
                      {token.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{token.description}</p>
                      )}
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Permissions</p>
                          <div className="flex flex-wrap gap-1">
                            {token.permissions.map((p) => (
                              <span key={p} className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Allowed Types</p>
                          <div className="flex flex-wrap gap-1">
                            {token.allowedTypes.map((t) => (
                              <span key={t} className="px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 capitalize">
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Created</p>
                          <p className="text-gray-500 dark:text-gray-400">{formatDate(token.createdAt)}</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Last Used</p>
                          <p className="text-gray-500 dark:text-gray-400">{formatDate(token.lastUsed)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showWizard && (
          <TokenWizard
            projects={projects}
            projectGroups={projectGroups}
            organizations={organizations}
            users={users}
            onClose={() => setShowWizard(false)}
            onCreated={(newToken: any) => {
              setRevealedToken(newToken.token ?? null);
              setExpandedId(newToken._id);
              fetchTokens();
              setShowWizard(false);
            }}
          />
        )}

        {/* Create Token Dialog */}
        {showCreate && (
          <CreateTokenDialog
            projects={projects}
            organizations={organizations}
            users={users}
            onClose={() => setShowCreate(false)}
            onCreated={(newToken) => {
              setRevealedToken(newToken.token ?? null);
              setExpandedId(newToken._id);
              fetchTokens();
              setShowCreate(false);
            }}
          />
        )}

        {/* Edit Token Dialog */}
        {editingToken && (
          <EditTokenDialog
            token={editingToken}
            projects={projects}
            organizations={organizations}
            users={users}
            onClose={() => setEditingToken(null)}
            onSaved={() => {
              setEditingToken(null);
              fetchTokens();
            }}
          />
        )}
      </div>
    </AuthGuard>
  );
}


/* ──────────────── Create Token Dialog ──────────────── */

interface CreateTokenDialogProps {
  projects: any[];
  organizations: any[];
  users: any[];
  onClose: () => void;
  onCreated: (token: ApiToken) => void;
}

/* ──────────────── Edit Token Dialog ──────────────── */

interface EditTokenDialogProps {
  token: ApiToken;
  projects: any[];
  organizations: any[];
  users: any[];
  onClose: () => void;
  onSaved: () => void;
}

function EditTokenDialog({ token, projects, organizations, users, onClose, onSaved }: EditTokenDialogProps) {
  const [name, setName] = useState(token.name);
  const [description, setDescription] = useState(token.description || '');
  const [accountType, setAccountType] = useState<'standard' | 'super_admin'>(token.accountType || 'standard');
  const [ownerUserId, setOwnerUserId] = useState(token.ownerUserId || '');
  const [projectId, setProjectId] = useState(token.projectId);
  const [projectIds, setProjectIds] = useState<string[]>(token.projectIds || []);
  const [permissions, setPermissions] = useState<string[]>(token.permissions || []);
  const [allowedTypes, setAllowedTypes] = useState<string[]>(token.allowedTypes || []);
  const [allowedRoutes, setAllowedRoutes] = useState<string[]>(token.allowedRoutes || []);
  const [saving, setSaving] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  // Group projects by organization
  const projectsByOrg = new Map<string, { orgName: string; projects: any[] }>();
  const unassigned: any[] = [];

  for (const project of projects) {
    if (project.organizationId) {
      const org = organizations.find((o: any) => o._id === project.organizationId);
      const orgName = org?.name || 'Unknown Organization';
      if (!projectsByOrg.has(project.organizationId)) {
        projectsByOrg.set(project.organizationId, { orgName, projects: [] });
      }
      projectsByOrg.get(project.organizationId)!.projects.push(project);
    } else {
      unassigned.push(project);
    }
  }

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const toggleType = (type: string) => {
    setAllowedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (accountType === 'standard' && !projectId) return;
    if (accountType === 'super_admin' && projectIds.length === 0) return;
    setSaving(true);
    try {
      const updates: any = {};
      if (name.trim() !== token.name) updates.name = name.trim();
      if (description.trim() !== (token.description || '')) updates.description = description.trim();
      if (ownerUserId && ownerUserId !== (token.ownerUserId || '')) updates.ownerUserId = ownerUserId;
      if (accountType !== (token.accountType || 'standard')) updates.accountType = accountType;
      if (accountType === 'standard' && projectId !== token.projectId) updates.projectId = projectId;
      if (accountType === 'super_admin' && JSON.stringify(projectIds) !== JSON.stringify(token.projectIds || [])) updates.projectIds = projectIds;
      if (JSON.stringify(permissions) !== JSON.stringify(token.permissions)) updates.permissions = permissions;
      if (JSON.stringify(allowedTypes) !== JSON.stringify(token.allowedTypes)) updates.allowedTypes = allowedTypes;
      if (JSON.stringify(allowedRoutes) !== JSON.stringify(token.allowedRoutes || [])) updates.allowedRoutes = allowedRoutes;

      if (Object.keys(updates).length === 0) {
        onClose();
        return;
      }

      await apiTokenApi.update(token._id, updates);
      toast.success('Token updated successfully');
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update token');
    } finally {
      setSaving(false);
    }
  };

  const selectedProject = projects.find((p) => p._id === projectId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center">
              <Pencil size={16} />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Token</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
          >
            <XCircle size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Token Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this token is used for"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner</label>
            <select
              value={ownerUserId}
              onChange={(e) => setOwnerUserId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              <option value="">No owner</option>
              {users.filter((u) => u.active).map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Type *</label>
            <div className="flex gap-3">
              <label
                className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  accountType === 'standard'
                    ? 'border-accent/30 bg-accent/5 text-accent'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500'
                }`}
              >
                <input type="radio" name="editAccountType" value="standard" checked={accountType === 'standard'} onChange={() => { setAccountType('standard'); setProjectIds([]); }} className="sr-only" />
                <Key size={14} />
                <div>
                  <p className="text-sm font-medium">Standard</p>
                  <p className="text-[10px] opacity-70">Single project access</p>
                </div>
              </label>
              <label
                className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  accountType === 'super_admin'
                    ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500'
                }`}
              >
                <input type="radio" name="editAccountType" value="super_admin" checked={accountType === 'super_admin'} onChange={() => { setAccountType('super_admin'); setProjectId(''); }} className="sr-only" />
                <Crown size={14} />
                <div>
                  <p className="text-sm font-medium">Super Admin</p>
                  <p className="text-[10px] opacity-70">Multi-project access</p>
                </div>
              </label>
            </div>
          </div>

          {/* Project Selection - Standard: single select */}
          {accountType === 'standard' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project *</label>
            {selectedProject ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-accent/5 border border-accent/20">
                <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{selectedProject.name}</span>
                <button
                  onClick={() => setProjectId('')}
                  className="text-xs text-accent hover:underline"
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Search projects..."
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                  {Array.from(projectsByOrg.entries()).map(([orgId, { orgName, projects: orgProjects }]) => {
                    const visible = orgProjects.filter((p) =>
                      p.name.toLowerCase().includes(projectSearch.toLowerCase())
                    );
                    if (visible.length === 0) return null;
                    return (
                      <div key={orgId}>
                        <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {orgName}
                        </div>
                        {visible.map((p) => (
                          <button
                            key={p._id}
                            onClick={() => setProjectId(p._id)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent/10 text-gray-900 dark:text-gray-100 transition-colors"
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                  {unassigned.filter((p) =>
                    p.name.toLowerCase().includes(projectSearch.toLowerCase())
                  ).length > 0 && (
                    <div>
                      <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Unassigned
                      </div>
                      {unassigned
                        .filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                        .map((p) => (
                          <button
                            key={p._id}
                            onClick={() => setProjectId(p._id)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent/10 text-gray-900 dark:text-gray-100 transition-colors"
                          >
                            {p.name}
                          </button>
                        ))}
                    </div>
                  )}
                  {filteredProjects.length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400">No projects found</p>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Project Selection - Super Admin: multi-select with Select All */}
          {accountType === 'super_admin' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Projects *</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Select one or more projects this token can access.
            </p>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Search projects..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
            <label className="flex items-center gap-2 px-3 py-1.5 mb-1 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <input
                type="checkbox"
                checked={projectIds.length === projects.length && projects.length > 0}
                onChange={() => {
                  if (projectIds.length === projects.length) setProjectIds([]);
                  else setProjectIds(projects.map((p) => p._id));
                }}
                className="rounded border-gray-300 text-accent focus:ring-accent/50"
              />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Select All ({projects.length})</span>
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
              {Array.from(projectsByOrg.entries()).map(([orgId, { orgName, projects: orgProjects }]) => {
                const visible = orgProjects.filter((p) =>
                  p.name.toLowerCase().includes(projectSearch.toLowerCase())
                );
                if (visible.length === 0) return null;
                return (
                  <div key={orgId}>
                    <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {orgName}
                    </div>
                    {visible.map((p) => (
                      <label key={p._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/5 transition-colors">
                        <input
                          type="checkbox"
                          checked={projectIds.includes(p._id)}
                          onChange={() => setProjectIds((prev) => prev.includes(p._id) ? prev.filter((id) => id !== p._id) : [...prev, p._id])}
                          className="rounded border-gray-300 text-accent focus:ring-accent/50"
                        />
                        <span className="text-xs text-gray-900 dark:text-gray-100">{p.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
              {unassigned.filter((p) =>
                p.name.toLowerCase().includes(projectSearch.toLowerCase())
              ).length > 0 && (
                <div>
                  <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Unassigned
                  </div>
                  {unassigned
                    .filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                    .map((p) => (
                      <label key={p._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/5 transition-colors">
                        <input
                          type="checkbox"
                          checked={projectIds.includes(p._id)}
                          onChange={() => setProjectIds((prev) => prev.includes(p._id) ? prev.filter((id) => id !== p._id) : [...prev, p._id])}
                          className="rounded border-gray-300 text-accent focus:ring-accent/50"
                        />
                        <span className="text-xs text-gray-900 dark:text-gray-100">{p.name}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>
            {projectIds.length > 0 && (
              <p className="text-xs text-accent mt-1">{projectIds.length} project{projectIds.length !== 1 ? 's' : ''} selected</p>
            )}
          </div>
          )}

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions</label>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_PERMISSIONS.map((perm) => (
                <label
                  key={perm.id}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs ${
                    permissions.includes(perm.id)
                      ? 'border-accent/30 bg-accent/5 text-accent'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                    className="sr-only"
                  />
                  <Shield size={12} />
                  {perm.label}
                </label>
              ))}
            </div>
          </div>

          {/* Allowed Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allowed Work Item Types</label>
            <div className="flex gap-2">
              {ALLOWED_TYPES.map((type) => (
                <label
                  key={type.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs ${
                    allowedTypes.includes(type.id)
                      ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={allowedTypes.includes(type.id)}
                    onChange={() => toggleType(type.id)}
                    className="sr-only"
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          {/* Route floor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Restrict to routes
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              One per line, e.g. <code>POST /api/leads</code>. Leave empty for no
              restriction. When set, the token may call <strong>only</strong> these
              routes &mdash; every other endpoint is refused regardless of the
              permissions above.
            </p>
            <textarea
              value={allowedRoutes.join('\n')}
              onChange={(e) => setAllowedRoutes(
                e.target.value.split('\n').map((r) => r.trim()).filter(Boolean))}
              rows={3}
              spellCheck={false}
              placeholder="POST /api/leads"
              className="w-full px-3 py-2 font-mono text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving || (accountType === 'standard' && !projectId) || (accountType === 'super_admin' && projectIds.length === 0)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CreateTokenDialog({ projects, organizations, users, onClose, onCreated }: CreateTokenDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [accountType, setAccountType] = useState<'standard' | 'super_admin'>('standard');
  const [projectId, setProjectId] = useState('');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [permissions, setPermissions] = useState<string[]>([
    'workitems:read', 'workitems:create', 'workitems:update',
    'activities:read', 'activities:create', 'projects:read',
  ]);
  const [allowedTypes, setAllowedTypes] = useState<string[]>(['epic', 'feature', 'story', 'task', 'bug', 'security', 'idea']);
  const [allowedRoutes, setAllowedRoutes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  // Group projects by organization
  const projectsByOrg = new Map<string, { orgName: string; projects: any[] }>();
  const unassigned: any[] = [];

  for (const project of projects) {
    if (project.organizationId) {
      const org = organizations.find((o: any) => o._id === project.organizationId);
      const orgName = org?.name || 'Unknown Organization';
      if (!projectsByOrg.has(project.organizationId)) {
        projectsByOrg.set(project.organizationId, { orgName, projects: [] });
      }
      projectsByOrg.get(project.organizationId)!.projects.push(project);
    } else {
      unassigned.push(project);
    }
  }

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const toggleType = (type: string) => {
    setAllowedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleCreate = async () => {
    if (!name.trim() || !ownerUserId) return;
    if (accountType === 'standard' && !projectId) return;
    if (accountType === 'super_admin' && projectIds.length === 0) return;
    setSaving(true);
    try {
      const payload: any = {
        name: name.trim(),
        description: description.trim(),
        accountType,
        ownerUserId,
        permissions,
        allowedTypes,
        allowedRoutes,
      };
      if (accountType === 'super_admin') {
        payload.projectIds = projectIds;
      } else {
        payload.projectId = projectId;
      }
      const result = await apiTokenApi.create(payload);
      toast.success('API token created — copy the token now!');
      onCreated(result);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create token');
    } finally {
      setSaving(false);
    }
  };

  const selectedProject = projects.find((p) => p._id === projectId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center">
              <Key size={16} />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create API Token</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
          >
            <XCircle size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Token Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. VS Code - John's Workstation"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this token is used for"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
            />
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner *</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              The user account that owns this token. Actions performed via this token will be attributed to this user.
            </p>
            <select
              value={ownerUserId}
              onChange={(e) => setOwnerUserId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              <option value="">Select owner...</option>
              {users.filter((u) => u.active).map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Type *</label>
            <div className="flex gap-3">
              <label
                className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  accountType === 'standard'
                    ? 'border-accent/30 bg-accent/5 text-accent'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500'
                }`}
              >
                <input type="radio" name="accountType" value="standard" checked={accountType === 'standard'} onChange={() => { setAccountType('standard'); setProjectIds([]); }} className="sr-only" />
                <Key size={14} />
                <div>
                  <p className="text-sm font-medium">Standard</p>
                  <p className="text-[10px] opacity-70">Single project access</p>
                </div>
              </label>
              <label
                className={`flex-1 flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  accountType === 'super_admin'
                    ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                    : 'border-gray-200 dark:border-gray-600 text-gray-500'
                }`}
              >
                <input type="radio" name="accountType" value="super_admin" checked={accountType === 'super_admin'} onChange={() => { setAccountType('super_admin'); setProjectId(''); }} className="sr-only" />
                <Crown size={14} />
                <div>
                  <p className="text-sm font-medium">Super Admin</p>
                  <p className="text-[10px] opacity-70">Multi-project access</p>
                </div>
              </label>
            </div>
          </div>

          {/* Project Selection - Standard: single select */}
          {accountType === 'standard' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project *</label>
            {selectedProject ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-accent/5 border border-accent/20">
                <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{selectedProject.name}</span>
                <button
                  onClick={() => setProjectId('')}
                  className="text-xs text-accent hover:underline"
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Search projects..."
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                  {Array.from(projectsByOrg.entries()).map(([orgId, { orgName, projects: orgProjects }]) => {
                    const visible = orgProjects.filter((p) =>
                      p.name.toLowerCase().includes(projectSearch.toLowerCase())
                    );
                    if (visible.length === 0) return null;
                    return (
                      <div key={orgId}>
                        <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {orgName}
                        </div>
                        {visible.map((p) => (
                          <button
                            key={p._id}
                            onClick={() => setProjectId(p._id)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent/10 text-gray-900 dark:text-gray-100 transition-colors"
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                  {unassigned.filter((p) =>
                    p.name.toLowerCase().includes(projectSearch.toLowerCase())
                  ).length > 0 && (
                    <div>
                      <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Unassigned
                      </div>
                      {unassigned
                        .filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                        .map((p) => (
                          <button
                            key={p._id}
                            onClick={() => setProjectId(p._id)}
                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent/10 text-gray-900 dark:text-gray-100 transition-colors"
                          >
                            {p.name}
                          </button>
                        ))}
                    </div>
                  )}
                  {filteredProjects.length === 0 && (
                    <p className="px-3 py-2 text-xs text-gray-400">No projects found</p>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Project Selection - Super Admin: multi-select with Select All */}
          {accountType === 'super_admin' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Projects *</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Select one or more projects this token can access.
            </p>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder="Search projects..."
                className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
            {/* Select All */}
            <label className="flex items-center gap-2 px-3 py-1.5 mb-1 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <input
                type="checkbox"
                checked={projectIds.length === projects.length && projects.length > 0}
                onChange={() => {
                  if (projectIds.length === projects.length) setProjectIds([]);
                  else setProjectIds(projects.map((p) => p._id));
                }}
                className="rounded border-gray-300 text-accent focus:ring-accent/50"
              />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Select All ({projects.length})</span>
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
              {Array.from(projectsByOrg.entries()).map(([orgId, { orgName, projects: orgProjects }]) => {
                const visible = orgProjects.filter((p) =>
                  p.name.toLowerCase().includes(projectSearch.toLowerCase())
                );
                if (visible.length === 0) return null;
                return (
                  <div key={orgId}>
                    <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {orgName}
                    </div>
                    {visible.map((p) => (
                      <label key={p._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/5 transition-colors">
                        <input
                          type="checkbox"
                          checked={projectIds.includes(p._id)}
                          onChange={() => setProjectIds((prev) => prev.includes(p._id) ? prev.filter((id) => id !== p._id) : [...prev, p._id])}
                          className="rounded border-gray-300 text-accent focus:ring-accent/50"
                        />
                        <span className="text-xs text-gray-900 dark:text-gray-100">{p.name}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
              {unassigned.filter((p) =>
                p.name.toLowerCase().includes(projectSearch.toLowerCase())
              ).length > 0 && (
                <div>
                  <div className="px-3 py-1 bg-gray-50 dark:bg-gray-700/50 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Unassigned
                  </div>
                  {unassigned
                    .filter((p) => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                    .map((p) => (
                      <label key={p._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/5 transition-colors">
                        <input
                          type="checkbox"
                          checked={projectIds.includes(p._id)}
                          onChange={() => setProjectIds((prev) => prev.includes(p._id) ? prev.filter((id) => id !== p._id) : [...prev, p._id])}
                          className="rounded border-gray-300 text-accent focus:ring-accent/50"
                        />
                        <span className="text-xs text-gray-900 dark:text-gray-100">{p.name}</span>
                      </label>
                    ))}
                </div>
              )}
            </div>
            {projectIds.length > 0 && (
              <p className="text-xs text-accent mt-1">{projectIds.length} project{projectIds.length !== 1 ? 's' : ''} selected</p>
            )}
          </div>
          )}

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions</label>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_PERMISSIONS.map((perm) => (
                <label
                  key={perm.id}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs ${
                    permissions.includes(perm.id)
                      ? 'border-accent/30 bg-accent/5 text-accent'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                    className="sr-only"
                  />
                  <Shield size={12} />
                  {perm.label}
                </label>
              ))}
            </div>
          </div>

          {/* Allowed Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Allowed Work Item Types</label>
            <div className="flex gap-2">
              {ALLOWED_TYPES.map((type) => (
                <label
                  key={type.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors text-xs ${
                    allowedTypes.includes(type.id)
                      ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                      : 'border-gray-200 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={allowedTypes.includes(type.id)}
                    onChange={() => toggleType(type.id)}
                    className="sr-only"
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>

          {/* Route floor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Restrict to routes
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              One per line, e.g. <code>POST /api/leads</code>. Leave empty for no
              restriction. When set, the token may call <strong>only</strong> these
              routes &mdash; every other endpoint is refused regardless of the
              permissions above.
            </p>
            <textarea
              value={allowedRoutes.join('\n')}
              onChange={(e) => setAllowedRoutes(
                e.target.value.split('\n').map((r) => r.trim()).filter(Boolean))}
              rows={3}
              spellCheck={false}
              placeholder="POST /api/leads"
              className="w-full px-3 py-2 font-mono text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || !ownerUserId || saving || (accountType === 'standard' && !projectId) || (accountType === 'super_admin' && projectIds.length === 0)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Creating...' : 'Create Token'}
          </button>
        </div>
      </div>
    </div>
  );
}
